import { Compass } from '@phosphor-icons/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import '../discovery.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { useSessionStore } from '@/lib/auth/session-store'

import { ActivityGrid } from '../components/activity-grid'
import { CityPickerSheet } from '../components/city-picker-sheet'
import { DiscoveryDatePresets } from '../components/date-presets'
import { DiscoveryHeader } from '../components/discovery-header'
import { DiscoveryNotice, type DiscoveryNoticeState } from '../components/discovery-notice'
import { DiscoveryToolbar } from '../components/discovery-toolbar'
import { EmptyState } from '../components/empty-state'
import { PullToRefresh } from '../components/pull-to-refresh'
import { FiltersSheet } from '../components/filters-sheet'
import { SearchField } from '../components/search-field'
import {
  activeFilterCount,
  type DiscoveryFilters,
} from '../filters/filters'
import { useDiscoveryFilterStore } from '../filters/filter-store'
import { useDiscoveryActivities } from '../hooks/use-activities'
import { useCategories } from '../hooks/use-categories'
import { useDiscoveryLocation } from '../hooks/use-discovery-location'
import { useIsDesktop } from '../hooks/use-is-desktop'
import { useUnreadNotificationCount } from '../hooks/use-unread-count'
import { useDebouncedValue } from '../hooks/ui'
import { resolveCityLabel } from '../location'
import { MapPanel } from '@/features/map/map-panel'
import type { Activity, DiscoveryLocation, DiscoveryView, MapBounds } from '../types'
import { discoveryApi } from '../api'

interface DiscoveryScreenProps {
  pageTitleKey: 'screens.home.title' | 'screens.activities.title'
}

/** Home/discovery list + map. Shared by `/home` and `/activities`. */
export function DiscoveryScreen({ pageTitleKey }: DiscoveryScreenProps) {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const userId = useSessionStore((state) => state.user?.id ?? null)
  const hydrate = useDiscoveryFilterStore((state) => state.hydrate)
  const filters = useDiscoveryFilterStore((state) => state.filters)
  const applyFilters = useDiscoveryFilterStore((state) => state.apply)
  const resetFilters = useDiscoveryFilterStore((state) => state.reset)
  const hydrated = useDiscoveryFilterStore(
    (state) => state.hydrated && state.userId === userId,
  )

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 350)
  const [view, setView] = useState<DiscoveryView>('list')
  const [bounds, setBounds] = useState<MapBounds | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [cityOpen, setCityOpen] = useState(false)
  const [pendingSaveIds, setPendingSaveIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [notice, setNotice] = useState<DiscoveryNoticeState | null>(null)

  const categoriesState = useCategories()
  const unreadCount = useUnreadNotificationCount()

  // Full-bleed map on mobile: the shell drops its bottom nav padding while
  // the map owns the space between the filters and the fixed bottom nav.
  useEffect(() => {
    if (view !== 'map') return
    const root = document.documentElement
    root.classList.add('discovery-map')
    return () => root.classList.remove('discovery-map')
  }, [view])

  useEffect(() => {
    if (userId) hydrate(userId)
  }, [hydrate, userId])

  const { ambientLocation, profileCity, detectLocation } = useDiscoveryLocation({
    userId,
    // A manually selected city is authoritative and must not trigger the
    // automatic permission prompt or overwrite the profile city.
    enabled: hydrated && !filters.location,
    filters,
  })

  // Explicit selected city always wins. The ambient fix only supplies a
  // radius center for the query; it is never written into the filter store.
  const queryFilters = useMemo<DiscoveryFilters>(() => {
    if (filters.location || !ambientLocation) return filters
    return {
      ...filters,
      location: {
        city: ambientLocation.city,
        latitude: ambientLocation.latitude,
        longitude: ambientLocation.longitude,
      },
    }
  }, [ambientLocation, filters])

  const { items, phase, refreshing, error, refresh, setItems } =
    useDiscoveryActivities({
      filters: queryFilters,
      search: debouncedSearch,
      bounds: view === 'map' ? bounds : null,
      enabled: hydrated,
    })

  const visibleItems = filters.savedOnly
    ? items.filter((activity) => activity.saved)
    : items
  const visibleSelectedId =
    selectedId && visibleItems.some((activity) => activity.id === selectedId)
      ? selectedId
      : null

  const initialCenter = useMemo(() => {
    const location = filters.location ?? ambientLocation
    if (!location) return null
    return {
      latitude: location.latitude,
      longitude: location.longitude,
    }
  }, [ambientLocation, filters.location])

  const handleToggleSave = useCallback(
    async (activity: Activity) => {
      if (pendingSaveIds.has(activity.id)) return
      const nextSaved = !activity.saved
      setPendingSaveIds((current) => new Set(current).add(activity.id))
      setItems((current) =>
        current.map((item) =>
          item.id === activity.id ? { ...item, saved: nextSaved } : item,
        ),
      )
      try {
        if (nextSaved) {
          await discoveryApi.saveActivity(activity.id)
        } else {
          await discoveryApi.unsaveActivity(activity.id)
        }
      } catch {
        setItems((current) =>
          current.map((item) =>
            item.id === activity.id ? { ...item, saved: activity.saved } : item,
          ),
        )
        setNotice({ messageKey: 'discovery.notice.saveError', tone: 'error' })
      } finally {
        setPendingSaveIds((current) => {
          const next = new Set(current)
          next.delete(activity.id)
          return next
        })
      }
    },
    [pendingSaveIds, setItems],
  )

  function handleViewChange(next: DiscoveryView) {
    setView(next)
    setSelectedId(null)
    if (next === 'list') setBounds(null)
  }

  function handleCitySelect(location: DiscoveryLocation) {
    setBounds(null)
    setSelectedId(null)
    applyFilters({ ...filters, location })
  }

  const handleDetectLocation = useCallback(async () => {
    await detectLocation()
    // Manual detection is ambient state, not a selected-city filter. Clear the
    // explicit city so the header/query follow the freshly detected location.
    const store = useDiscoveryFilterStore.getState()
    if (store.filters.location) {
      setBounds(null)
      setSelectedId(null)
      store.apply({ ...store.filters, location: null })
    }
  }, [detectLocation])

  function handleApplyFilters(next: DiscoveryFilters) {
    if (
      next.location?.latitude !== filters.location?.latitude ||
      next.location?.longitude !== filters.location?.longitude
    ) {
      setBounds(null)
      setSelectedId(null)
    }
    applyFilters(next)
    setFiltersOpen(false)
  }

  function handleResetFilters() {
    setBounds(null)
    setSelectedId(null)
    resetFilters()
    setFiltersOpen(false)
  }

  const cityLabel = resolveCityLabel(
    {
      explicitCity: filters.location?.city,
      ambientCity: ambientLocation?.city,
      profileCity,
    },
    t('discovery.city.fallback'),
  )
  const activeFilters = activeFilterCount(filters)

  return (
    <section
      aria-labelledby="discovery-title"
      className={`content-width pb-10${view === 'map' ? ' discovery-screen--map' : ''}`}
    >
      <h1 id="discovery-title" className="sr-only">
        {t(pageTitleKey)}
      </h1>

      <div className="discovery-sticky">
        <DiscoveryHeader
          cityLabel={cityLabel}
          onOpenCity={() => setCityOpen(true)}
          unreadCount={unreadCount}
        />

        <div className="mt-1 flex items-center gap-2">
          <SearchField
            value={search}
            onChange={setSearch}
            className="min-w-0 flex-1"
          />
          <DiscoveryToolbar
            iconOnly
            resultCount={visibleItems.length}
            view={view}
            onViewChange={handleViewChange}
            refreshing={refreshing}
            onRefresh={refresh}
            showRefresh={isDesktop}
            activeFilters={activeFilters}
            onOpenFilters={() => setFiltersOpen(true)}
          />
        </div>

        <div className="mt-2 md:mt-3">
          <DiscoveryDatePresets
            value={filters.dateRange}
            onChange={(dateRange) => applyFilters({ ...filters, dateRange })}
          />
        </div>

      </div>

      {view === 'map' ? (
        <div className="discovery-map-wrap mt-4">
          <MapPanel
            activities={visibleItems}
            selectedId={visibleSelectedId}
            initialCenter={initialCenter}
            loading={phase === 'loading'}
            error={error}
            onRetry={refresh}
            onSelect={setSelectedId}
            onBoundsChange={setBounds}
            onShowList={() => handleViewChange('list')}
          />
        </div>
      ) : (
        <PullToRefresh
          className="mt-2"
          onRefresh={refresh}
          refreshing={refreshing}
          disabled={isDesktop}
        >
          {error && items.length > 0 ? (
            <div
              role="alert"
              className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-danger-soft px-4 py-3 text-sm font-semibold text-danger"
            >
              <span>{t('discovery.error.body')}</span>
              <button
                type="button"
                className="btn-ghost min-h-11 px-2"
                onClick={refresh}
              >
                {t('common.retry')}
              </button>
            </div>
          ) : null}

          {!hydrated || phase === 'loading' || phase === 'idle' ? (
            <div className="mt-4">
              <LoadingState labelKey="discovery.loading" />
            </div>
          ) : phase === 'error' ? (
            <div className="mt-6">
              <ErrorState
                error={error}
                titleKey="discovery.error.title"
                bodyKey="discovery.error.body"
                onRetry={refresh}
              />
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon={<Compass size={28} weight="bold" aria-hidden="true" />}
                titleKey="discovery.empty.title"
                bodyKey="discovery.empty.body"
              />
            </div>
          ) : (
            <div className="mt-4">
              <ActivityGrid
                activities={visibleItems}
                onToggleSave={(activity) => void handleToggleSave(activity)}
                pendingSaveIds={pendingSaveIds}
              />
            </div>
          )}
        </PullToRefresh>
      )}

      {filtersOpen ? (
        <FiltersSheet
          open
          onClose={() => setFiltersOpen(false)}
          filters={filters}
          categories={categoriesState.categories}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      ) : null}

      {cityOpen ? (
        <CityPickerSheet
          open
          onClose={() => setCityOpen(false)}
          onSelect={handleCitySelect}
          onDetectLocation={handleDetectLocation}
        />
      ) : null}

      <DiscoveryNotice notice={notice} onDismiss={() => setNotice(null)} />
    </section>
  )
}
